import {
  type TenantConfig,
  DEFAULT_TENANT_CONFIG,
  deepMerge,
  resolveChainFields,
} from '@workspace/tenant-config';
import { tenantIdFromHost } from '@/lib/tenant-host';

export type { TenantConfig } from '@workspace/tenant-config';
export type {
  TenantIdentity,
  TenantLanding,
  TenantTheme,
  TenantLinks,
  TenantContracts,
  TenantWallets,
  TenantYield,
  TenantTrust,
  TenantChain,
  TenantFeatures,
  TenantSubscription,
  AnubisMigration,
  MigrationPhase,
  TenantProtocol,
} from '@workspace/tenant-config';

function loadTenantJson(): Partial<TenantConfig> {
  try {
    const modules = import.meta.glob('/tenant.json', { eager: true }) as Record<string, { default?: Partial<TenantConfig> }>;
    const entry = Object.values(modules)[0];
    if (entry?.default) return entry.default;
    if (entry && typeof entry === 'object') return entry as Partial<TenantConfig>;
  } catch {
    // no tenant.json found
  }
  return {};
}

function loadTenantConfig(): TenantConfig {
  const env = import.meta.env;

  const jsonOverrides = loadTenantJson();
  const c = deepMerge(
    DEFAULT_TENANT_CONFIG as unknown as Record<string, unknown>,
    jsonOverrides as Record<string, unknown>,
  ) as unknown as TenantConfig;

  if (env.VITE_TENANT_NAME) c.identity.name = env.VITE_TENANT_NAME;
  if (env.VITE_TENANT_TAGLINE) c.identity.tagline = env.VITE_TENANT_TAGLINE;
  if (env.VITE_TENANT_LOGO_PATH) c.identity.logoSvgPath = env.VITE_TENANT_LOGO_PATH;
  if (env.VITE_TENANT_PRIMARY_COLOR) c.theme.primaryColorHex = env.VITE_TENANT_PRIMARY_COLOR;

  if (env.VITE_TELEGRAM_LINK) c.links.telegram = env.VITE_TELEGRAM_LINK;
  if (env.VITE_WHATSAPP_LINK) c.links.whatsapp = env.VITE_WHATSAPP_LINK;
  if (env.VITE_FACEBOOK_LINK) c.links.facebook = env.VITE_FACEBOOK_LINK;
  if (env.VITE_DISCORD_LINK) c.links.discord = env.VITE_DISCORD_LINK;
  if (env.VITE_CERTIK_AUDIT_URL) c.links.certikAudit = env.VITE_CERTIK_AUDIT_URL;
  if (env.VITE_BACKOFFICE_URL) c.links.backoffice = env.VITE_BACKOFFICE_URL;
  if (env.VITE_ORIGIN_URL) c.links.website = env.VITE_ORIGIN_URL;

  if (env.VITE_SLGNS_CONTRACT) c.contracts.slgns = env.VITE_SLGNS_CONTRACT;
  if (env.VITE_LGNS_CONTRACT) c.contracts.lgns = env.VITE_LGNS_CONTRACT;

  if (env.VITE_PAYMENT_WALLET) c.wallets.payment = env.VITE_PAYMENT_WALLET;
  if (env.VITE_ADMIN_WALLETS) {
    c.wallets.admin = env.VITE_ADMIN_WALLETS.split(',').map((a: string) => a.trim().toLowerCase()).filter(Boolean);
  }

  if (env.VITE_CHAIN_ID) c.chain.chainId = parseInt(env.VITE_CHAIN_ID, 10);

  resolveChainFields(c);

  return c;
}

export let tenantConfig: Readonly<TenantConfig> = Object.freeze(
  loadTenantConfig()
);

const configListeners: Array<(config: TenantConfig) => void> = [];

export function onTenantConfigUpdate(listener: (config: TenantConfig) => void) {
  configListeners.push(listener);
}

export async function fetchAndApplyTenantConfig(): Promise<TenantConfig> {
  try {
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
    const res = await fetch(`${apiBase}/api/v1/tenant-config`, {
      headers: { 'X-Tenant-ID': tenantIdFromHost() },
    });
    if (!res.ok) return tenantConfig;
    const data = await res.json();
    if (data?.config) {
      const merged = deepMerge(
        tenantConfig as unknown as Record<string, unknown>,
        data.config as Record<string, unknown>,
      ) as unknown as TenantConfig;
      resolveChainFields(merged);
      tenantConfig = Object.freeze(merged);
      for (const listener of configListeners) {
        listener(tenantConfig);
      }
    }
  } catch {
  }
  return tenantConfig;
}
